import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'wouter';
import { useUpdateProfile } from '../../lib/useProfile.js';
import './conta.css';

/**
 * Linhas da página "Conta" e das suas subpáginas, no molde do PageShell
 * (`pgs-row`): uma que leva a outra tela, uma que faz algo e uma chave.
 * Frase do Secretário em cada uma; nada de nome de sistema.
 */

export function LinkRow({
  href,
  icon,
  title,
  hint,
  tag,
  quiet = false,
}: {
  href: string;
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
  /** Selo curto à direita (ex.: nome do plano). */
  tag?: ReactNode;
  /** Linha discreta (ex.: apagar a conta). */
  quiet?: boolean;
}) {
  return (
    <Link href={href} className={`pgs-row ct-row${quiet ? ' ct-quiet' : ''}`}>
      {icon && <span className="pgs-ic">{icon}</span>}
      <span className="pgs-rowt">
        <b>{title}</b>
        {hint && <small>{hint}</small>}
      </span>
      {tag && <span className="ct-tag">{tag}</span>}
      <span className="ct-arrow" aria-hidden="true">
        ›
      </span>
    </Link>
  );
}

export function ActionRow({
  onClick,
  icon,
  title,
  hint,
  disabled = false,
  quiet = false,
}: {
  onClick: () => void;
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
  disabled?: boolean;
  quiet?: boolean;
}) {
  return (
    <button
      type="button"
      className={`pgs-row ct-row${quiet ? ' ct-quiet' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {icon && <span className="pgs-ic">{icon}</span>}
      <span className="pgs-rowt">
        <b>{title}</b>
        {hint && <small>{hint}</small>}
      </span>
      <span className="ct-arrow" aria-hidden="true">
        ›
      </span>
    </button>
  );
}

export function ToggleRow({
  on,
  onToggle,
  icon,
  title,
  hint,
  disabled = false,
}: {
  on: boolean;
  onToggle: () => void;
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className={`pgs-row ct-row${on ? ' on' : ''}`}
      role="switch"
      aria-checked={on}
      onClick={onToggle}
      disabled={disabled}
    >
      {icon && <span className="pgs-ic">{icon}</span>}
      <span className="pgs-rowt">
        <b>{title}</b>
        {hint && <small>{hint}</small>}
      </span>
      <span className="ct-switch" aria-hidden="true" />
    </button>
  );
}

/** Linha fixa, sem ação: o que é sempre assim (ex.: proteção de ponta a ponta). */
export function FixedRow({
  icon,
  title,
  hint,
  seal = 'Sempre',
}: {
  icon?: ReactNode;
  title: string;
  hint?: ReactNode;
  seal?: string;
}) {
  return (
    <div className="pgs-row ct-row">
      {icon && <span className="pgs-ic">{icon}</span>}
      <span className="pgs-rowt">
        <b>{title}</b>
        {hint && <small>{hint}</small>}
      </span>
      <span className="ct-seal">{seal}</span>
    </div>
  );
}

/** Guarda uma preferência do perfil e diz qual chave está salvando. */
export function useSetPref() {
  const update = useUpdateProfile();
  const [saving, setSaving] = useState<string | null>(null);
  async function setPref(patch: Record<string, unknown>, key: string) {
    if (saving) return;
    setSaving(key);
    try {
      await update.mutateAsync({ preferences: patch });
    } catch {
      /* a chave volta sozinha: o perfil não mudou */
    } finally {
      setSaving(null);
    }
  }
  return { saving, setPref };
}
